import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store';
import {
  createMatter,
  fetchMatterDropdowns,
  deleteMatter,
} from '../../store/actions/Matters/MatterActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { showAddClientSidePanel } from '../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import apis from '../../utils/apis';
import { fetchDataSources } from '../../store/actions/AIStudioAction';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import FormSkeleton from '../shared/FormComponents/FormSkeleton';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import ProgressTracker from './ProgressTracker';
import ClientSelector from '../shared/ClientSelector';
import { Collaborator } from '../shared/CollaboratorSelector';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import LogoSpinner from '../shared/LogoSpinner';
import Toggle from '../shared/TailwindComponents/Toggle';
import { hasCasesSupport } from './matterConstants';
import IndexSourceSelector from './IndexSourceSelector';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import {
  PiScalesLight,
  PiGavelLight,
  PiDatabaseLight,
  PiListChecksLight,
  PiChatTextLight,
  PiTextAlignLeftLight,
  PiNoteLight,
  PiFileTextLight,
  PiSparkleLight,
} from 'react-icons/pi';
import FileOrDescriptionSelector from '../shared/FileOrDescriptionSelector';
import MatterIntentSelector, { MatterIntent } from './MatterIntentSelector';
import MatterWizard, { MatterWizardData } from './MatterWizard';
import Button from '../shared/TailwindComponents/Button';
import { AVAILABLE_JURISDICTIONS } from './matterConstants';
import { FeatureTour, WizardStepTip, matterIntentTourSteps, matterIntentTip, matterQuickCreateTip } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

interface AnalysisOptionsValue {
  fetchLaws: boolean;
  fetchPrecedents: boolean;
  fetchDataSource: boolean;
  createTasks: boolean;
  createQuestionnaire: boolean;
  generateDescription: boolean;
  generateNotes: boolean;
  generateDraftSuggestions: boolean;
  generateInsightSuggestions: boolean;
}

const DEFAULT_ANALYSIS_OPTIONS: AnalysisOptionsValue = {
  fetchLaws: true,
  fetchPrecedents: true,
  fetchDataSource: true,
  createTasks: true,
  createQuestionnaire: true,
  generateDescription: true,
  generateNotes: true,
  generateDraftSuggestions: true,
  generateInsightSuggestions: true,
};

const AnalysisOptionsSelector: React.FC<{
  value: AnalysisOptionsValue;
  onChange: (value: AnalysisOptionsValue) => void;
  disabled?: boolean;
  hasDataSources?: boolean;
  jurisdiction?: string;
}> = ({ value, onChange, disabled, hasDataSources, jurisdiction }) => {
  const { t } = useTranslation();
  const opts = value || DEFAULT_ANALYSIS_OPTIONS;
  const showCases = hasCasesSupport(jurisdiction);

  const toggleOption = (key: keyof AnalysisOptionsValue) => {
    onChange({ ...opts, [key]: !opts[key] });
  };

  const researchOptions: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }[] = [
    { key: 'fetchLaws', label: t('Applicable Laws'), description: t('Search for relevant statutes and legislation'), icon: <PiScalesLight className="w-5 h-5" /> },
    ...(showCases ? [{ key: 'fetchPrecedents' as const, label: t('Precedents / Case Law'), description: t('Search for relevant case law and precedents'), icon: <PiGavelLight className="w-5 h-5" /> }] : []),
    ...(hasDataSources ? [{ key: 'fetchDataSource' as const, label: t('Connected Data Sources'), description: t('Search your proprietary document collections'), icon: <PiDatabaseLight className="w-5 h-5" /> }] : []),
  ];

  const generationOptions: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }[] = [
    { key: 'createTasks', label: t('Suggested Tasks'), description: t('AI-generated action items and next steps'), icon: <PiListChecksLight className="w-5 h-5" /> },
    { key: 'createQuestionnaire', label: t('Client Questionnaire'), description: t('AI-generated questions for client intake'), icon: <PiChatTextLight className="w-5 h-5" /> },
  ];

  const aiGenerationOptions: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }[] = [
    { key: 'generateDescription', label: t('Matter Description'), description: t('AI-generated summary and case description'), icon: <PiTextAlignLeftLight className="w-5 h-5" /> },
    { key: 'generateNotes', label: t('Case Notes'), description: t('AI-generated notes and key observations'), icon: <PiNoteLight className="w-5 h-5" /> },
    { key: 'generateDraftSuggestions', label: t('Draft Suggestions'), description: t('Suggested documents to draft for this matter'), icon: <PiFileTextLight className="w-5 h-5" /> },
    { key: 'generateInsightSuggestions', label: t('Insight Suggestions'), description: t('Suggested analyses and insights to generate'), icon: <PiSparkleLight className="w-5 h-5" /> },
  ];

  const renderOptionCard = (opt: { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode }) => (
    <div
      key={opt.key}
      className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-all duration-200 ${
        opts[opt.key]
          ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`flex-shrink-0 transition-colors duration-200 ${
          opts[opt.key]
            ? 'text-primary-600 dark:text-primary-400'
            : 'text-gray-400 dark:text-gray-500'
        }`}>
          {opt.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{opt.label}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{opt.description}</p>
        </div>
      </div>
      <div className="flex-shrink-0">
        <Toggle enabled={opts[opt.key]} onToggle={() => toggleOption(opt.key)} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">{t('Legal Research')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {researchOptions.map(renderOptionCard)}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">{t('AI Generation')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {generationOptions.map(renderOptionCard)}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">{t('Content Generation')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {aiGenerationOptions.map(renderOptionCard)}
        </div>
      </div>
    </div>
  );
};

interface MatterFormProps {
  onClose: () => void;
  clientId?: string;
  onGroupJoined?: (groupName: string) => void;
}

interface Person {
  id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
}

interface MatterFormValues {
  matterTitle: string;
  category: string;
  language: string;
  jurisdiction: string;
  clients: any[];
  collaborators: Collaborator[];
}

const MatterForm: React.FC<MatterFormProps> = ({ onClose, clientId, onGroupJoined }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { hasAiFeatureAccess } = useLicenseGate();
  const location = useLocation();

  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const people = useSelector((state: RootState) => state.orgPeople?.people as Person[] || []);
  const { isPreparingDoc, isConnecting, progress, progressPercent, isRunning } = useSelector(
    (state: RootState) => state.matters
  );

  // Intent: read from URL query param (?intent=quick|analyze), falls back to null (selector screen)
  const queryIntent = new URLSearchParams(location.search).get('intent') as MatterIntent | null;
  const [intent, setIntent] = useState<MatterIntent | null>(
    queryIntent === 'quick' || queryIntent === 'analyze' ? queryIntent : null
  );

  // Sync intent changes to URL
  const updateIntent = useCallback((newIntent: MatterIntent | null) => {
    setIntent(newIntent);
    const params = new URLSearchParams(location.search);
    if (newIntent) {
      params.set('intent', newIntent);
    } else {
      params.delete('intent');
    }
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [history, location.pathname, location.search]);
  const [clients, setClients] = useState<any[]>([]);
  const [clientRoleOptions, setClientRoleOptions] = useState<DropdownOption[]>([]);
  const [languages, setLanguages] = useState<DropdownOption[]>([]);
  const [jurisdictions, setJurisdictions] = useState<DropdownOption[]>([]);
  const [categories, setCategories] = useState<DropdownOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoAddClient, setAutoAddClient] = useState<{ ClientID: string; ClientRoles: string[] } | null>(null);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [defaultJurisdiction, setDefaultJurisdiction] = useState<string>('');
  const [dataSources, setDataSources] = useState<DropdownOption[]>([]);
  // Title persists across intent switches
  const [persistedTitle, setPersistedTitle] = useState<string>('');

  // ── Intent selection tour (first time user sees Quick Create vs Analyze) ──
  const {
    isRunning: isIntentTourRunning,
    handleJoyrideCallback: handleIntentTourCallback,
  } = useFeatureTour({
    tourId: 'matter-intent',
    steps: matterIntentTourSteps,
    readyToShow: !intent && !isLoadingData,
    autoStartDelay: 500,
  });

  const matterIdRef = useRef<string | undefined>(undefined);
  const correlationIdRef = useRef<string | null>(null);
  const matterGroupRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingData(true);
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns());
        if (dropdownsData) {
          setLanguages(dropdownsData.languages || []);
          setJurisdictions(AVAILABLE_JURISDICTIONS);
          setClientRoleOptions(dropdownsData.clientRoles || []);

          // Use AI settings as default selected values
          const aiSettings = dropdownsData.aiSettings;
          if (aiSettings) {
            if (aiSettings.language) setDefaultLanguage(aiSettings.language);
            if (aiSettings.jurisdiction) setDefaultJurisdiction(aiSettings.jurisdiction);
          }
        }

        const categoriesResponse = await apis({
          method: 'GET',
          url: '/dropdown/matter-categories',
          params: { active_only: true }
        });

        if (categoriesResponse?.data?.data) {
          const formattedCategories = categoriesResponse.data.data.map((cat: any) => ({
            value: String(cat.id || cat.category_id || ''),
            label: cat.name || cat.category_name || ''
          }));
          setCategories(formattedCategories);
        }

        const clientsResponse = await apis({
          method: 'GET',
          url: '/clients',
          params: { page: '1', page_size: '100', is_active: 'true' }
        });

        if (clientsResponse?.data?.data) {
          setClients(clientsResponse.data.data);
        }

        // Fetch available data sources for the organization (only if AI feature is available)
        if (hasAiFeatureAccess('data_source_indexing')) {
          try {
            const dsResult: any = await dispatch(fetchDataSources({ page: 1, limit: 100 }));
            if (dsResult?.dataSources) {
              const readySources = dsResult.dataSources
                .filter((ds: any) => {
                  const idxStatus = (ds.indexer_status || '').toLowerCase();
                  const dsStatus = (ds.status || '').toLowerCase();
                // Accept datasources that have been fully indexed and are active/ready
                return (
                  ['success', 'completed'].includes(idxStatus) ||
                  ['ready', 'active'].includes(dsStatus)
                );
              })
              .map((ds: any) => ({
                value: ds.uuid,
                label: ds.name || 'Unnamed Data Source'
              }));
            setDataSources(readySources);
          }
        } catch (dsError) {
          console.warn('[MatterForm] Failed to fetch data sources:', dsError);
        }
        }

        await dispatch(fetchOrgPeople());

        // Fetch linkable projects
        try {
          const projects = await dispatch(fetchLinkableProjects());
          if (projects && Array.isArray(projects)) {
            setProjectOptions(projects.map((p: any) => ({ value: p.uuid, label: p.title })));
          }
        } catch (projErr) {
          // Non-critical
        }
      } catch (error: any) {
        console.error('[MatterForm] Error loading data:', error?.message);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [dispatch]);

  const buildClientOptions = (): DropdownOption[] => {
    return clients
      .filter(client => client.is_active !== false)
      .map(client => ({
        value: String(client.uuid || client.ClientID || client.id || ''),
        label: String(client.Name || client.name || 'Unknown Client')
      }));
  };

  const handleAddClientClick = () => {
    const onClientCreated = (newClient: any) => {
      const clientId = String(newClient.uuid || newClient.id || '');
      const clientName = newClient.name || 'Unknown Client';

      (async () => {
        try {
          const clientsResponse = await apis({
            method: 'GET',
            url: '/clients',
            params: { page: '1', page_size: '15' }
          });

          if (clientsResponse?.data?.data) {
            setClients(clientsResponse.data.data);
          }

          // Auto-select the newly created client in the ClientSelector
          if (clientId) {
            // Small delay to ensure clients state + clientOptions have re-rendered
            setTimeout(() => {
              setAutoAddClient({ ClientID: clientId, ClientRoles: [] });
            }, 100);
          }

          dispatch(
            showFeedbackModal({
              showModal: true,
              message: `Client "${clientName}" created successfully.`,
              modalType: 'success',
              duration: 3000
            })
          );
        } catch (err: any) {
          console.error('Failed to refetch clients:', err?.message);
        }
      })();
    };

    dispatch(showAddClientSidePanel(onClientCreated));
  };

  const handleAiProcessingMessage = useCallback(
    (raw: any) => {
      const event = Array.isArray(raw) ? raw[0] : raw || {};
      const innerData = event.data || {};
      const data = { ...event, ...innerData };

      const eventCorrelationId = data.correlationId || data.correlation_id || data.CorrelationID || null;
      const eventMatterUuid = data.matter_uuid || data.matterUuid || data.MatterUUID || null;

      if (eventCorrelationId && !correlationIdRef.current) {
        correlationIdRef.current = eventCorrelationId;
      }

      if (eventMatterUuid && !matterIdRef.current) {
        matterIdRef.current = eventMatterUuid;
      }

      const rawStatus = data.status ?? '';
      const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
      const subType = data.sub_type || data.event_name || data.eventType || data.event_type || null;
      const stepName = data.step_name || data.stepName || data.step || null;

      const isSystemInit = subType === 'matter_initiated' || subType === 'matter.initiated' || (status === 'started' && Array.isArray(data.steps));
      const isCompletion = status === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
      const isFailure = status === 'failed' || data.error;
      const isWelcome = stepName === 'welcome' || subType === 'welcome' || data.message?.toLowerCase().includes('preparing');
      const isTechnicalMessage = (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') && !data.message;

      if (isTechnicalMessage) {
        return;
      }

      if (isWelcome) {
        dispatch({ type: 'MATTER_CREATION_CONNECTED' });
        return;
      }

      if (isSystemInit) {
        const normalizedSteps = (data.steps || []).map((s: any) => ({
          step: s.step || s.step_name || 'Matter Creation',
          status: s.status || 'pending',
          messages: s.messages || [],
        }));

        const idToUse = eventCorrelationId || correlationIdRef.current;
        if (idToUse && data.startTime) {
          localStorage.setItem(`matter_creation_start:${idToUse}`, data.startTime);
        }

        dispatch({
          type: 'MATTER_CREATION_PROGRESS_INIT',
          payload: { steps: normalizedSteps }
        });
        dispatch({ type: 'MATTER_CREATION_CONNECTED' });
        return;
      }

      const message = data.message || '';
      const elapsedSeconds = data.elapsed_seconds;
      const iconIdentifier = data.icon_identifier;
      const mappedPayload = {
        step: stepName || 'Matter Creation',
        message,
        status: data.status,
        progress_percent: data.progress_percent,
        elapsedSeconds,
        iconIdentifier,
        ...data,
      };
      const hasRealStep = Boolean(stepName);

      if (subType === 'step_started') {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_STARTED',
            payload: {
              step: mappedPayload.step,
              elapsedSeconds,
              iconIdentifier,
              progress_percent: data.progress_percent,
            },
          });

          if (mappedPayload.message) {
            dispatch({
              type: 'MATTER_CREATION_STEP_MESSAGE',
              payload: {
                ...mappedPayload,
                elapsedSeconds,
                iconIdentifier,
                progress_percent: data.progress_percent,
              },
            });
          }
        }
        return;
      }

      if (subType === 'step_progress') {
        if (mappedPayload.message) {
          dispatch({
            type: 'MATTER_CREATION_STEP_MESSAGE',
            payload: {
              ...mappedPayload,
              elapsedSeconds,
              iconIdentifier,
              progress_percent: data.progress_percent,
            },
          });
        }
        return;
      }

      if (subType === 'step_completed') {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_COMPLETED',
            payload: {
              step: mappedPayload.step,
              message: mappedPayload.message || 'Step completed.',
              elapsedSeconds,
              iconIdentifier,
              progress_percent: data.progress_percent,
              ...mappedPayload,
            },
          });
        }
        return;
      }

      if (isCompletion) {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_COMPLETED',
            payload: {
              step: mappedPayload.step,
              message: mappedPayload.message || 'Matter creation completed.',
              progress_percent: 100,
              ...mappedPayload,
            },
          });
        }

        dispatch({
          type: 'MATTER_CREATION_COMPLETE',
          payload: { ...mappedPayload, progress_percent: 100 },
        });

        // If the form has already been closed / unmounted, don't navigate or show modals.
        // The list-page handler (Matters.tsx) will handle completion from here.
        if (!mountedRef.current) return;

        // Use matter_uuid from event data as fallback if matterIdRef wasn't set
        const completedMatterUuid = matterIdRef.current || data.matter_uuid || data.matterUuid;

        const suggestedTasks = data.suggested_tasks || mappedPayload.suggested_tasks || [];

        // if (suggestedTasks && suggestedTasks.length > 0) {
        //   const taskCreationMatterUuid = completedMatterUuid;

        //   dispatch(showModal({
        //     type: 'suggestedTasks',
        //     tasks: suggestedTasks,
        //     message: 'Create suggested tasks for this matter?',
        //     subMsg: 'We\'ve identified some action items based on the matter details. You can create them now or skip.',
        //     matterUuid: taskCreationMatterUuid,
        //     onConfirm: async (selectedTasks: any[]) => {
        //       if (!selectedTasks || selectedTasks.length === 0) {
        //         return;
        //       }

        //       try {
        //         const tasksForCreation = selectedTasks.map(task => ({
        //           title: task.TaskTitle,
        //           description: task.TaskDescription,
        //           due_date: task.TaskDeadline,
        //           status: 'todo',
        //           priority: 'medium'
        //         }));

        //         if (!taskCreationMatterUuid) {
        //           throw new Error('Matter UUID is required for task creation');
        //         }

        //         const result = await dispatch(createBulkTasks(tasksForCreation, taskCreationMatterUuid));

        //         if (result && result.created_count > 0) {
        //           dispatch(showFeedbackModal({
        //             showModal: true,
        //             message: `Created ${result.created_count} of ${selectedTasks.length} tasks successfully.${result.failed_count > 0 ? ` ${result.failed_count} failed.` : ''}`,
        //             modalType: 'success',
        //             duration: 3000
        //           }));
        //         } else {
        //           dispatch(showFeedbackModal({
        //             modalType: 'error',
        //             message: 'No tasks were created. Please try again.',
        //             showModal: true,
        //             duration: 3000
        //           }));
        //         }
        //       } catch (error) {
        //         dispatch(showFeedbackModal({
        //           modalType: 'error',
        //           message: 'Failed to create some or all tasks. Please try again.',
        //           showModal: true,
        //           duration: 3000
        //         }));
        //       }

        //       dispatch(hideModal());
        //       history.push(`/matters/${completedMatterUuid}`);
        //     },
        //     onCancel: () => {
        //       dispatch(hideModal());
        //       history.push(`/matters/${completedMatterUuid}`);
        //     },
        //     showModal: false
        //   }));
        // } else {
          history.push(`/matters/${completedMatterUuid}`);
        // }
        return;
      }

      if (isFailure) {
        const errorMessage = data.error || mappedPayload.message || 'AI Processing failed';

        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_FAILED',
            payload: {
              step: mappedPayload.step || 'Matter Creation',
              message: errorMessage,
              ...mappedPayload,
            },
          });
        }

        dispatch({
          type: 'MATTER_CREATION_FAILED',
          payload: mappedPayload,
        });
        return;
      }

      if (mappedPayload.message) {
        if (hasRealStep) {
          dispatch({
            type: 'MATTER_CREATION_STEP_MESSAGE',
            payload: mappedPayload,
          });
        } else {
          if (mappedPayload.progress_percent !== undefined) {
            dispatch({
              type: 'MATTER_CREATION_STEP_MESSAGE',
              payload: {
                ...mappedPayload,
                step: 'Matter Processing',
              },
            });
          }
        }
      }
    },
    [dispatch]
  );

  // ── Quick Create submit (BaseForm) ──
  const handleQuickCreateSubmit = async (formData: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('MatterName', formData.matterTitle || '');
      if (formData.category) payload.append('CategoryID', String(formData.category));
      payload.append('Language', String(formData.language || defaultLanguage || 'en'));
      payload.append('Jurisdiction', String(formData.jurisdiction || defaultJurisdiction || 'US'));

      if (formData.clients && Array.isArray(formData.clients)) {
        const validClients = formData.clients.filter(
          (c: any) => c.ClientID && String(c.ClientID).trim() !== ''
        );
        if (validClients.length > 0) {
          payload.append('Clients', JSON.stringify(validClients));
        }
      }

      if (formData.collaborators && formData.collaborators.length > 0) {
        const mappedCollaborators = formData.collaborators.map((collab: any) => ({
          user_uuid: collab.collaborator_uuid || collab.user_id,
          access: collab.access
        }));
        payload.append('Collaborators', JSON.stringify(mappedCollaborators));
      }

      if (formData.projectUUID) {
        payload.append('project_uuid', formData.projectUUID);
      }

      payload.append('skip_processing', 'true');

      // Handle optional file uploads
      const filesOrDesc = formData.filesOrDescription || {};
      const files = filesOrDesc.files || [];
      const description = filesOrDesc.description || '';

      if (files.length > 0) {
        files.forEach((file: File) => {
          payload.append('bulk_files', file);
        });
      } else if (description.trim()) {
        const descriptionBlob = new Blob([description], { type: 'text/plain' });
        const descriptionFile = new File([descriptionBlob], 'matter_description.txt', { type: 'text/plain' });
        payload.append('bulk_files', descriptionFile);
      }

      const newMatter = await dispatch(createMatter(payload));
      const matterUuid = newMatter?.matter?.uuid || newMatter?.uuid;

      if (!matterUuid) {
        throw new Error('Matter created but no UUID returned');
      }

      setIsSubmitting(false);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Matter created successfully'),
          modalType: 'success',
          duration: 3000
        })
      );
      history.push(`/matters/${matterUuid}`);
    } catch (error: any) {
      console.error('[MatterForm] Quick create error:', error);
      setIsSubmitting(false);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to create matter. Please try again.'),
          modalType: 'error',
          duration: 5000
        })
      );
    }
  };

  // ── Wizard submit (Create & Analyze) ──
  const handleWizardSubmit = async (data: MatterWizardData) => {
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('MatterName', data.matterTitle);
      if (data.category) payload.append('CategoryID', String(data.category));
      payload.append('Language', String(data.language || defaultLanguage || 'en'));
      payload.append('Jurisdiction', String(data.jurisdiction || defaultJurisdiction || 'US'));

      if (data.clients.length > 0) {
        const validClients = data.clients.filter(
          (c) => c.ClientID && String(c.ClientID).trim() !== ''
        );
        if (validClients.length > 0) {
          payload.append('Clients', JSON.stringify(validClients));
        }
      }

      if (data.collaborators.length > 0) {
        const mappedCollaborators = data.collaborators.map((collab: any) => ({
          user_uuid: collab.collaborator_uuid || collab.user_id,
          access: collab.access
        }));
        payload.append('Collaborators', JSON.stringify(mappedCollaborators));
      }

      if (data.projectUUID) {
        payload.append('project_uuid', data.projectUUID);
      }

      const files = data.filesOrDescription.files || [];
      const description = data.filesOrDescription.description || '';

      if (files.length > 0) {
        files.forEach((file: File) => {
          payload.append('bulk_files', file);
        });
      } else if (description.trim()) {
        const descriptionBlob = new Blob([description], { type: 'text/plain' });
        const descriptionFile = new File([descriptionBlob], 'matter_description.txt', { type: 'text/plain' });
        payload.append('bulk_files', descriptionFile);
      }

      if (data.dataSources.length > 0) {
        payload.append('DataSourceUUIDs', JSON.stringify(data.dataSources));
      }

      if (data.selectedIndexes.length > 0) {
        payload.append('SelectedIndexes', JSON.stringify(data.selectedIndexes));
      }

      const opts = data.analysisOptions;
      payload.append('FetchApplicableLaws', String(opts.fetchLaws ?? true));
      payload.append('FetchPrecedents', String(opts.fetchPrecedents ?? true));
      payload.append('FetchDataFromOwnDataSource', String(opts.fetchDataSource ?? true));
      payload.append('CreateTasks', String(opts.createTasks ?? true));
      payload.append('CreateQuestionnaire', String(opts.createQuestionnaire ?? true));

      const newMatter = await dispatch(createMatter(payload));
      const matterUuid = newMatter?.matter?.uuid || newMatter?.uuid;
      const correlationId =
        newMatter?.processing?.correlationId ||
        newMatter?.processing?.correlation_id ||
        newMatter?.correlation_id ||
        newMatter?.correlationId;

      if (!matterUuid) {
        throw new Error('Matter creation did not return a UUID');
      }

      matterIdRef.current = matterUuid;

      if (correlationId) {
        correlationIdRef.current = correlationId;
        const multiHubService = getMultiHubSocketService();
        const group = `matter_${correlationId}`;
        matterGroupRef.current = group;

        try {
          await multiHubService.connectToHub('ai_processing');
          multiHubService.emit('ai_processing', 'join', { group });
          multiHubService.on('ai_processing', '*', handleAiProcessingMessage);

          localStorage.setItem(`matter_creation_start:${correlationId}`, Date.now().toString());

          dispatch({
            type: 'CONNECTING_MATTER_CREATION_SERVICE',
            payload: {
              isConnecting: true,
              matter_id: matterUuid,
              correlation_id: correlationId,
            },
          });
        } catch (pubsubError) {
          console.error('[MatterForm] Failed to connect to PubSub:', pubsubError);
          history.push(`/matters/${matterUuid}`);
        }
      } else {
        history.push(`/matters/${matterUuid}`);
      }

      setIsSubmitting(false);
    } catch (error: any) {
      console.error('[MatterForm] Analyze submission error:', error);
      setIsSubmitting(false);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to create matter. Please try again.'),
          modalType: 'error',
          duration: 5000
        })
      );
    }
  };

  const handleCancelMatterCreation = useCallback(async () => {
    try {
      const multiHubService = getMultiHubSocketService();
      if (matterGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: matterGroupRef.current });
      }
      multiHubService.off('ai_processing', '*', handleAiProcessingMessage);

      if (matterIdRef.current) {
        try {
          await dispatch(deleteMatter(matterIdRef.current));
        } catch (error) {
          console.error('[MatterForm] Failed to delete cancelled matter:', error);
        }
      }

      dispatch({ type: 'MATTER_CREATION_CANCELLED' });
      setIsSubmitting(false);
      onClose();
    } catch (error) {
      console.error('[MatterForm] Cancel error:', error);
      dispatch({ type: 'MATTER_CREATION_CANCELLED' });
      setIsSubmitting(false);
      onClose();
    }
  }, [dispatch, onClose, handleAiProcessingMessage]);

  // ── Always reset matter-creation Redux state + PubSub on unmount ──
  useEffect(() => {
    return () => {
      mountedRef.current = false;

      const multiHubService = getMultiHubSocketService();
      if (matterGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: matterGroupRef.current });
      }
      multiHubService.off('ai_processing', '*', handleAiProcessingMessage);

      dispatch({ type: 'MATTER_CREATION_CANCELLED' });
    };
  }, [dispatch, handleAiProcessingMessage]);

  // ── Quick Create: BaseForm sections & schema ──
  const quickCreateSections: BaseFormSectionConfig[] = useMemo(() => {
    return [
      {
        fields: [
          {
            name: 'matterTitle',
            type: 'input',
            label: t('Matter Title'),
            placeholder: t('e.g. Smith v. Johnson, IP License Review, Tenant Dispute...'),
            required: true,
            gridSize: 'full'
          } as BaseFormFieldConfig
        ]
      },
      ...(projectOptions.length > 0 ? [{
        fields: [
          {
            name: 'projectUUID',
            type: 'dropdown' as const,
            label: t('Link to Project'),
            placeholder: t('Select a project (optional)'),
            required: false,
            customProps: { fullWidth: true },
            gridSize: 'full' as const,
            options: projectOptions,
          } as BaseFormFieldConfig,
        ],
      }] : []),
      {
        title: t('Attach Files'),
        description: t('Optionally upload documents now — you can always add more later.'),
        fields: [
          {
            name: 'filesOrDescription',
            type: 'custom',
            label: t('Files'),
            customComponent: FileOrDescriptionSelector,
            required: false,
            gridSize: 'full'
          } as BaseFormFieldConfig
        ]
      },
    ];
  }, [t, projectOptions]);

  const quickCreateSchema = useMemo(() => {
    const s = new FormSchema('QuickCreateMatterSchema');
    s.addField(new FormField('matterTitle', 'text', true, t('Matter Title'), {
      customValidator: (value: any) => {
        if (!value || !String(value).trim()) {
          return { isValid: false, error: t('Matter title is required') };
        }
        return { isValid: true, sanitized: String(value).trim() };
      }
    }));
    s.addField(new FormField('filesOrDescription', 'text', false, t('Files')));
    return s;
  }, [t]);

  // Persist title when switching intents
  const handleQuickCreateValuesChange = useCallback((values: Record<string, any>, changedField: string) => {
    if (changedField === 'matterTitle') {
      setPersistedTitle(values.matterTitle || '');
    }
  }, []);

  // ── Render ──
  if (isLoadingData) {
    return <FormSkeleton />;
  }

  if (isSubmitting || ((isPreparingDoc || isConnecting || isRunning) && progress.length === 0)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <LogoSpinner size="large" />
      </div>
    );
  }

  if (isPreparingDoc || isConnecting || progress.length > 0 || isRunning) {
    return (
      <div className="my-auto h-screen flex justify-center items-center">
        <ProgressTracker
          steps={progress}
          isComplete={false}
          title={t('Creating Matter')}
          matterId={matterIdRef.current || 'Matter'}
          correlationId={correlationIdRef.current ?? undefined}
          isPreparingDoc={isPreparingDoc}
          isConnecting={isConnecting}
          isRunning={isRunning}
          backendPercentage={progressPercent}
          onCancel={handleCancelMatterCreation}
        />
      </div>
    );
  }

  // ── Intent selection screen ──
  if (!intent) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto custom-scrollbar-main">
          <div className="max-w-2xl mx-auto px-6 py-10">
            <div className="text-center mb-8">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('Create a New Matter')}
              </h1>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('How would you like to get started?')}
              </p>
            </div>
            <WizardStepTip
              tipId="matter-intent-choice"
              title={matterIntentTip.title}
              content={matterIntentTip.content}
            />
            <MatterIntentSelector value={null} onChange={updateIntent} />
          </div>
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-6 py-4">
          <div className="mx-auto w-full flex justify-end gap-3 max-w-2xl">
            <Button
              type="button"
              onClick={onClose}
              variant='neutral'
              size='small'
            >
              {t('Cancel')}
            </Button>
          </div>
        </div>
        <FeatureTour
          steps={matterIntentTourSteps}
          run={isIntentTourRunning}
          onCallback={handleIntentTourCallback}
          disableOverlay
        />
      </div>
    );
  }

  // ── Create & Analyze wizard ──
  if (intent === 'analyze') {
    return (
      <MatterWizard
        initialTitle={persistedTitle}
        categories={categories}
        languages={languages}
        jurisdictions={jurisdictions}
        clientOptions={buildClientOptions()}
        clientRoleOptions={clientRoleOptions}
        dataSourceOptions={dataSources}
        projectOptions={projectOptions}
        defaultLanguage={defaultLanguage}
        defaultJurisdiction={defaultJurisdiction}
        autoAddClient={autoAddClient}
        onAddNewClient={handleAddClientClick}
        onSubmit={handleWizardSubmit}
        onCancel={onClose}
        onBack={() => updateIntent(null)}
        isSubmitting={isSubmitting}
      />
    );
  }

  // ── Quick Create form ──
  return (
    <div className="flex flex-col h-full">
      <div className="max-w-3xl mx-auto w-full px-6 pt-4">
        <WizardStepTip
          tipId="matter-quick-create"
          title={matterQuickCreateTip.title}
          content={matterQuickCreateTip.content}
        />
      </div>
      <BaseForm
        title={t('Quick Create')}
        description={t('Create a matter instantly. You can add clients, collaborators, and run PONS analysis anytime from the matter page.')}
        sections={quickCreateSections}
        schema={quickCreateSchema}
        initialValues={{
          matterTitle: persistedTitle,
          filesOrDescription: { files: [], description: '' },
        }}
        onSubmit={handleQuickCreateSubmit}
        onCancel={onClose}
        onBack={() => updateIntent(null)}
        submitLabel={t('Create Matter')}
        cancelLabel={t('Cancel')}
        backLabel={t('Back')}
        maxWidth="max-w-3xl"
        onValuesChange={handleQuickCreateValuesChange}
      />
    </div>
  );
};

export default MatterForm;
